// Centralized content for IdeaClap Global website

export const BRAND = {
    name: "IdeaClap Global",
    short: "IdeaClap",
    tagline: "Applauding Creative Brains",
    promise: "Creative + Marketing, powered by AI Agents and Human Talent",
    logoUrl: "/logo-transparent.png",
    contact: {
        email: "info@IdeaClap.com",
        phone: "(908) 213-5755",
        address: "35 West 31st Street, New York, NY, USA",
    },
};

export const IMAGES = {
    hero: "/generated/hero-light.jpg",
    talentNetwork: "/generated/talent-network.jpg",
    aboutMood: "/generated/about-mood.jpg",
    motifSpark: "/generated/motif-spark.jpg",
};

export const NAV_LINKS = [
    { label: "Home", to: "/" },
    { label: "Capabilities", to: "/capabilities" },
    { label: "Talent Network", to: "/talent" },
    { label: "Work", to: "/work" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
];

export const STATS = [
    { value: 240, suffix: "+", label: "Curated specialists in network" },
    { value: 38, suffix: "", label: "Markets activated globally" },
    { value: 4.6, suffix: "x", decimals: 1, label: "Median pipeline lift" },
    { value: 92, suffix: "%", label: "Client retention beyond year one" },
];

export const CLIENT_LOGOS = [
    "NORTHWIND",
    "Helio Labs",
    "OBSIDIAN&CO",
    "Lumera",
    "FIELDNOTE",
    "Parallel",
    "VERSO",
    "Atlas&Kin",
    "PRISM/UX",
    "Marrowbone",
    "NORTHSTAR",
    "Cadence",
];

export const CAPABILITIES = [
    {
        id: "creative-systems",
        eyebrow: "01 / Creative Systems",
        title: "Creative Systems",
        kicker: "Brand, narrative, and design as compounding infrastructure.",
        description:
            "We build durable creative systems — visual identity, narrative architecture, and modular content frameworks — that make every campaign sharper than the last.",
        bullets: [
            "Brand & narrative architecture",
            "Design systems and motion language",
            "Editorial content frameworks",
        ],
        accent: "blue",
        size: "lg",
    },
    {
        id: "ai-agent-workflows",
        eyebrow: "02 / AI Agent Workflows",
        title: "AI Agent Workflows",
        kicker: "Agentic systems that compress creative and ops cycles 10x.",
        description:
            "From research to production to QA, we deploy multi-agent workflows that handle the predictable work — so humans focus on judgment, taste, and strategy.",
        bullets: [
            "Research, brief, and ideation agents",
            "Production & post-production pipelines",
            "QA, brand-safety, and review loops",
        ],
        accent: "blue",
        size: "md",
    },
    {
        id: "human-talent",
        eyebrow: "03 / Human Talent Network",
        title: "Human Talent Network",
        kicker: "A curated, opinionated network of operators — not a marketplace.",
        description:
            "Strategists, designers, engineers, growth leads, and creative directors — vetted, briefed by AI, and orchestrated against a clear outcome.",
        bullets: [
            "Senior strategists & creative directors",
            "Brand, product, and motion designers",
            "Growth, lifecycle, and ops engineers",
        ],
        accent: "gold",
        size: "md",
    },
    {
        id: "growth-engines",
        eyebrow: "04 / Growth Engines",
        title: "Growth Engines",
        kicker: "Demand systems that compound — measurable, modular, owned by you.",
        description:
            "We build the operating system for your growth — content engines, lifecycle programs, paid systems, and analytics that close the loop on every dollar.",
        bullets: [
            "Content & SEO engines",
            "Lifecycle & CRM programs",
            "Paid systems and attribution",
        ],
        accent: "blue",
        size: "lg",
    },
];

export const HOW_IT_WORKS = [
    {
        id: "agents",
        step: "01",
        title: "AI Agents Accelerate",
        accent: "blue",
        description:
            "Multi-agent workflows do the heavy lift — research, briefs, drafts, production, QA — at machine speed and zero idle time.",
        chips: ["Research", "Briefs", "Drafts", "QA"],
    },
    {
        id: "humans",
        step: "02",
        title: "Human Talent Elevates",
        accent: "gold",
        description:
            "Senior creative directors, strategists, and operators take the agent output and apply taste, judgment, and craft — the things that compound brand equity.",
        chips: ["Strategy", "Craft", "Judgment", "Direction"],
    },
    {
        id: "growth",
        step: "03",
        title: "Growth Systems Scale",
        accent: "blue",
        description:
            "Outputs ship into measurable, modular growth engines — content, lifecycle, paid, analytics — that you own and that compound week over week.",
        chips: ["Pipeline", "Lifecycle", "Paid", "Attribution"],
    },
];

export const USE_CASES = [
    {
        id: "launch-brand",
        title: "Launch a Brand",
        description: "From positioning and identity to a launch motion engineered for compound demand.",
        outcomes: ["Positioning, narrative, identity", "Launch site + content engine v1"],
    },
    {
        id: "scale-content",
        title: "Scale Content Creation",
        description: "Stand up an agentic content factory: briefs, drafts, design, and review in a continuous loop.",
        outcomes: ["50–500 pieces / month with quality", "Editorial governance & brand-safety"],
    },
    {
        id: "deploy-assistants",
        title: "Deploy AI Assistants",
        description: "Internal copilots and customer-facing assistants — designed, deployed, and supervised by humans.",
        outcomes: ["Sales, support, ops copilots", "Guardrails, evals, observability"],
    },
    {
        id: "demand-engines",
        title: "Build Demand Engines",
        description: "Compounding pipeline programs across content, lifecycle, and paid — instrumented end to end.",
        outcomes: ["Pipeline + qualified-meeting growth", "Closed-loop attribution"],
    },
    {
        id: "prototype-ideas",
        title: "Prototype New Ideas",
        description: "Validate, prototype, and ship new product surfaces with a small, senior, AI-augmented team.",
        outcomes: ["Concept → prototype in weeks", "Customer signal before commitment"],
    },
];

export const CASE_STUDIES = [
    {
        id: "northwind-leadgen",
        client: "Northwind",
        category: "AI-Powered Lead Generation",
        title: "From flat top-of-funnel to a self-improving demand system",
        summary:
            "We replaced a stalled outbound motion with an agentic lead-generation system that learns from every reply and routes only qualified meetings to the AE team.",
        challenge:
            "A growth-stage SaaS firm was burning rep cycles on cold lists with low conversion and no learning loop.",
        approach:
            "Deployed a five-agent pipeline (research → enrichment → message → reply parsing → routing), supervised by a senior growth lead. Tuned weekly against close rates.",
        outcome:
            "Pipeline rebuilt in 6 weeks. Reply-to-meeting conversion 3.2x. SDR ramp time cut in half. The system is now an owned asset, not a vendor relationship.",
        metrics: [
            { label: "Pipeline lift", value: "4.6x" },
            { label: "Reply→meeting", value: "3.2x" },
            { label: "SDR ramp", value: "-52%" },
        ],
        cover: "/generated/case-northwind.jpg",
        tags: ["Demand", "AI Agents", "Growth Engine"],
    },
    {
        id: "helio-launch",
        client: "Helio Labs",
        category: "Brand + Campaign Launch",
        title: "A consumer launch that earned attention, not just impressions",
        summary:
            "Built positioning, identity, and a launch motion for a sleep-tech category challenger — designed for cultural traction, not just paid reach.",
        challenge:
            "A well-funded consumer product team had strong product-market signal but no narrative and no launch system.",
        approach:
            "Three-week sprint to ship positioning, identity, hero film, launch site, and a coordinated content + influencer + paid push, supervised by a senior creative director.",
        outcome:
            "Launch week generated 18M earned impressions, sold-out first run, and built an editorial system the in-house team now operates.",
        metrics: [
            { label: "Earned impressions", value: "18M" },
            { label: "Pre-orders", value: "sold out" },
            { label: "Time-to-launch", value: "3 weeks" },
        ],
        cover: "/generated/case-helio.jpg",
        tags: ["Brand", "Launch", "Creative Systems"],
    },
    {
        id: "obsidian-ops",
        client: "Obsidian & Co.",
        category: "Agentic Operations Workflow",
        title: "Replacing 60 hours of ops with a supervised agent stack",
        summary:
            "A high-touch services firm was drowning in client onboarding, intake, and reporting. We rebuilt the workflow around supervised agents with human approval points.",
        challenge:
            "Senior partners were spending 60+ hours/week on ops they couldn't bill — and quality was slipping.",
        approach:
            "Mapped the workflow, designed an agent stack for intake, scheduling, drafting, and reporting, with three human checkpoints. Custom dashboards + observability.",
        outcome:
            "Ops time down 78%. Partners reclaimed billable hours. Onboarding NPS up 31 points.",
        metrics: [
            { label: "Ops time", value: "-78%" },
            { label: "Onboarding NPS", value: "+31" },
            { label: "Billable recovery", value: "42 hrs/wk" },
        ],
        cover: "/generated/case-obsidian.jpg",
        tags: ["AI Agents", "Operations", "Systems"],
    },
    {
        id: "lumera-content",
        client: "Lumera",
        category: "Content & Growth Engine",
        title: "From episodic posts to a compounding editorial system",
        summary:
            "Stood up a full editorial engine for a digital-first brand — strategy, agentic production, senior editing, and distribution — running 14 channels.",
        challenge:
            "Founder-led content was peaky and unsustainable; CAC was rising as paid scaled.",
        approach:
            "Editorial strategy + agent-led production with senior editor-in-chief oversight. Built a daily ops cadence, briefs library, and analytics dashboard.",
        outcome:
            "Organic traffic 3.4x in 5 months. CAC down 37%. The team owns the engine, not us.",
        metrics: [
            { label: "Organic", value: "3.4x" },
            { label: "CAC", value: "-37%" },
            { label: "Channels live", value: "14" },
        ],
        cover: "/generated/case-lumera.jpg",
        tags: ["Content", "Growth Engine", "Editorial"],
    },
];

export const TESTIMONIALS = [
    { quote: "IdeaClap helped us move from fragmented marketing execution to a scalable growth system we actually own.", name: "Priya Raghavan", title: "VP Growth, Northwind" },
    { quote: "What we got wasn't a campaign — it was an operating system for creativity and growth, supervised by people who clearly care.", name: "Marcus Hale", title: "CMO, Helio Labs" },
    { quote: "Their AI workflows didn't replace our team — they let our senior people do senior work again.", name: "Anika Bose", title: "Founding Partner, Obsidian & Co." },
    { quote: "They obsessed about taste and judgment as much as the technology. That's the whole point, and almost nobody gets it right.", name: "Jordan Vega", title: "Head of Brand, Lumera" },
    { quote: "The clearest articulation we've heard of how AI and human talent should actually work together at a creative company.", name: "Elena Park", title: "Partner, Cadence Ventures" },
    { quote: "Editorial-grade craft, engineering rigor, and the humility of operators who've actually run growth orgs.", name: "Daniel Okafor", title: "Founder, Parallel" },
];

export const TALENT_DISCIPLINES = [
    { title: "Creative Direction", description: "Brand, narrative, art and motion direction with editorial sensibility." },
    { title: "Strategy", description: "Positioning, GTM strategy, content strategy, and growth modeling." },
    { title: "Design", description: "Brand, product, motion, and editorial design — at studio caliber." },
    { title: "AI Engineering", description: "Agent architecture, evals, retrieval, observability, and safety." },
    { title: "Growth & Lifecycle", description: "Demand programs, lifecycle CRM, and paid systems with attribution." },
    { title: "Editorial", description: "Senior editors and writers who shape category-defining narratives." },
];

export const TALENT_VALUES = [
    { title: "Curation, not roster", description: "We don't bid jobs to a marketplace. We assemble small, opinionated teams matched to outcome." },
    { title: "Judgment is the product", description: "Agents accelerate; people decide. The work is shaped by people whose taste is the moat." },
    { title: "Operators, not order-takers", description: "Every specialist has shipped, scaled, and been on the hook for outcomes — not just deliverables." },
    { title: "Global, time-zone fluent", description: "Teams that operate across geographies as a feature, not a friction point." },
];

export const ABOUT_BLOCKS = [
    {
        eyebrow: "Why now",
        title: "AI made execution cheap. Judgment is the new moat.",
        body: "Most creative and marketing work is being commoditized by AI. The companies that win will pair agentic systems with senior human judgment — and treat the combination as durable infrastructure, not a vendor cost line.",
    },
    {
        eyebrow: "Mission",
        title: "Make creative + growth systems that compound.",
        body: "We build engines, not deliverables. Every project leaves a measurable, owned asset behind — a content engine, a demand system, an agent stack — that gets stronger over time.",
    },
    {
        eyebrow: "Operating principles",
        title: "Editorial craft. Engineering rigor. Operator humility.",
        body: "We treat brand and product with the same rigor we treat code and agents. We measure what matters, ship in weeks, and partner like operators — not consultants.",
    },
];

export const TIMELINE_OPTIONS = [
    "ASAP — within 2 weeks",
    "This quarter",
    "Next quarter",
    "Exploratory — no fixed date",
];

export const BUDGET_OPTIONS = [
    "<$25k",
    "$25k – $75k",
    "$75k – $200k",
    "$200k – $500k",
    "$500k+",
    "Not sure yet",
];

export const CAPABILITY_OPTIONS = ["Creative", "Marketing", "AI Agents", "Talent"];
